import {
  applyGuidanceEvent,
  applyProfileEvent,
  type GuidanceEventInput,
  type GuidanceItemState,
  type GuidanceKindKey,
  type GuidanceProfileState,
} from '@bookora/shared';

import type {
  GuidanceItemResponse,
  GuidanceProfileResponse,
  GuidanceStateResponse,
} from '@/api/generated/model';

/**
 * The generated response types mark nullable columns optional; the shared
 * fold works on exact `| null` shapes. These convert at the boundary, and
 * `applyToState` is the optimistic write the panel makes before the server
 * answers — the SAME fold the server runs, so a failed save never re-offers
 * a tour in this tab.
 */

export function toItemState(item: GuidanceItemResponse): GuidanceItemState {
  return {
    kind: item.kind,
    key: item.key,
    instanceKey: item.instanceKey ?? '',
    version: item.version,
    status: item.status,
    lastStep: item.lastStep ?? null,
    stepCount: item.stepCount ?? null,
    impressions: item.impressions,
    startedCount: item.startedCount,
    completedCount: item.completedCount,
    snoozeCount: item.snoozeCount,
    dismissCount: item.dismissCount,
    shownDays: item.shownDays,
    lastShownDayKey: item.lastShownDayKey ?? null,
    firstOfferedAt: item.firstOfferedAt ?? null,
    lastOfferedAt: item.lastOfferedAt ?? null,
    lastStartedAt: item.lastStartedAt ?? null,
    completedAt: item.completedAt ?? null,
    dismissedAt: item.dismissedAt ?? null,
    snoozedUntil: item.snoozedUntil ?? null,
    acceptedAt: item.acceptedAt ?? null,
    goalReachedAt: item.goalReachedAt ?? null,
  };
}

export function toProfileState(profile: GuidanceProfileResponse): GuidanceProfileState {
  return {
    cohort: profile.cohort,
    autoOfferTours: profile.autoOfferTours,
    consecutiveDeclines: profile.consecutiveDeclines,
    autoOfferPausedUntil: profile.autoOfferPausedUntil ?? null,
    lastProactiveAt: profile.lastProactiveAt ?? null,
    proactiveDayKey: profile.proactiveDayKey ?? null,
    proactiveToday: profile.proactiveToday,
    tipsEnabled: profile.tipsEnabled,
    tipsPausedUntil: profile.tipsPausedUntil ?? null,
    mutedTipCategories: [...profile.mutedTipCategories],
    newTipsDayKey: profile.newTipsDayKey ?? null,
    newTipsToday: profile.newTipsToday,
    consecutiveTipDismissals: profile.consecutiveTipDismissals,
    tipBackoffUntil: profile.tipBackoffUntil ?? null,
    createdAt: profile.createdAt,
  };
}

/** The item of `(kind, key, instanceKey)` in a state response, or null. */
export function findItem(
  state: GuidanceStateResponse | undefined,
  kind: GuidanceKindKey,
  key: string,
  instanceKey = '',
): GuidanceItemState | null {
  const item = state?.items.find(
    (i) => i.kind === kind && i.key === key && (i.instanceKey ?? '') === instanceKey,
  );
  return item ? toItemState(item) : null;
}

/**
 * Folds one event into a cached state. TIP events pass through untouched: tip
 * state is not part of `GET /guidance`, the guide's own endpoints own it.
 */
export function applyToState(
  state: GuidanceStateResponse,
  ev: GuidanceEventInput,
  now: Date,
): GuidanceStateResponse {
  if (ev.kind === 'TIP') return state;
  const instanceKey = ev.instanceKey ?? '';
  const index = state.items.findIndex(
    (i) => i.kind === ev.kind && i.key === ev.key && (i.instanceKey ?? '') === instanceKey,
  );
  const prev = index === -1 ? null : toItemState(state.items[index]);
  const next = applyGuidanceEvent(prev, ev, now);
  const profile = applyProfileEvent(toProfileState(state.profile), ev, now, prev);

  let items = state.items;
  if (next && next !== prev) {
    const response: GuidanceItemResponse = { ...next, updatedAt: now.toISOString() };
    items =
      index === -1 ? [...items, response] : items.map((it, i) => (i === index ? response : it));
  }
  return { ...state, items, profile };
}
